import React, { useRef } from "react";
import { Instagram, Facebook, ArrowUpRight, ShoppingBag, FileText } from "lucide-react";
import { trackEvent } from "@/lib/api";

const PLATFORMS = [
  { key: "just_eat_url", label: "Just Eat", class: "je" },
  { key: "deliveroo_url", label: "Deliveroo", class: "dlv" },
  { key: "uber_eats_url", label: "Uber Eats", class: "uber" },
  { key: "panda_url", label: "Panda", class: "panda" },
];

// TikTok icon (lucide doesn't ship one, use inline svg)
const TikTokIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.85a8.16 8.16 0 0 0 4.77 1.52V6.92a4.85 4.85 0 0 1-1.84-.23z" />
  </svg>
);

export default function BrandCard({ brand }) {
  // Hook must be called unconditionally (rules of hooks).
  const tiltRef = useRef(null);

  if (brand.is_coming_soon) {
    return (
      <div
        className="brand-card ghost min-h-[420px] p-8 flex flex-col items-center justify-center text-center"
        style={{ "--brand-color": brand.color }}
        data-testid={`brand-card-coming-soon-${brand.name}`}
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted mb-4">
          Coming Soon
        </span>
        <span className="font-display text-[64px] text-muted leading-none">
          {brand.logo_letter || "#"}
        </span>
        <span className="font-display text-[22px] mt-3 text-[var(--text)]/70">
          {brand.name}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted mt-2">
          A new brand moving in
        </span>
      </div>
    );
  }

  const handleClick = (platform) => () =>
    trackEvent("platform_click", {
      brand_id: brand.id,
      brand_name: brand.name,
      platform,
    });

  const handleWebsite = () =>
    trackEvent("website_click", { brand_id: brand.id, brand_name: brand.name });

  const handleSocial = (platform) => () =>
    trackEvent("social_click", {
      brand_id: brand.id,
      brand_name: brand.name,
      platform,
    });

  // Subtle 3D tilt on hover (skip on touch devices / reduced motion users)
  const handleTiltMove = (e) => {
    const el = tiltRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (window.matchMedia("(hover: none)").matches) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    const rx = (0.5 - y) * 6;   // max ±3deg
    const ry = (x - 0.5) * 8;   // max ±4deg
    el.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-3px)`;
    el.style.setProperty("--glare-x", `${x * 100}%`);
    el.style.setProperty("--glare-y", `${y * 100}%`);
  };
  const handleTiltLeave = () => {
    const el = tiltRef.current;
    if (!el) return;
    el.style.transform = "";
    el.style.removeProperty("--glare-x");
    el.style.removeProperty("--glare-y");
  };

  // Defaults so newly-added brands without all fields filled don't look broken
  const brandColor = brand.color && brand.color !== "#000000" ? brand.color : "#C8A97E";
  const logoLetter = brand.logo_letter || brand.name.slice(0, 2).toUpperCase();

  return (
    <article
      ref={tiltRef}
      onMouseMove={handleTiltMove}
      onMouseLeave={handleTiltLeave}
      className="brand-card brand-card--tilt"
      style={{ "--brand-color": brandColor }}
      data-testid={`brand-card-${brand.name.replace(/\s+/g, "-")}`}
    >
      <div className="photo">
        {brand.image ? (
          <img
            src={brand.image}
            alt={`${brand.name} signature dish`}
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = "none";
              e.currentTarget.parentElement.classList.add("photo-fallback");
            }}
          />
        ) : (
          <div className="w-full h-full" />
        )}
        {brand.is_halal && (
          <span className="halal-badge" data-testid={`brand-${brand.name.replace(/\s+/g, "-")}-halal-badge`}>
            Halal
          </span>
        )}
        {brand.is_opening_soon && (
          <span className="opening-soon-stamp" data-testid={`brand-${brand.name.replace(/\s+/g, "-")}-opening-soon-stamp`} aria-label="Opening soon">
            Opening Soon
          </span>
        )}
        <div className="photo-overlay" aria-hidden />
        <div className="stripe" aria-hidden />
      </div>

      <div className="p-5 flex flex-col flex-1">
        {/* Identity row */}
        <div className="flex items-center gap-3.5">
          <div className="logo-box">{logoLetter}</div>
          <div className="min-w-0 flex-1">
            <h3
              className="font-display text-[26px] leading-tight truncate"
              style={{ color: brandColor }}
            >
              {brand.name}
            </h3>
            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted truncate">
              {brand.cuisine}
            </p>
          </div>
        </div>

        {/* Description */}
        <p className="mt-4 text-[13.5px] leading-[1.55] text-[var(--text)]/75 font-light">
          {brand.description}
        </p>

        <div className="divider my-4" />

        {/* Order on */}
        <div>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted block mb-2.5">
            Order On
          </span>
          <div className="flex gap-1.5">
            {PLATFORMS.map((p) => {
              const url = brand[p.key];
              if (url) {
                return (
                  <a
                    key={p.key}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`plat-btn ${p.class}`}
                    onClick={handleClick(p.label)}
                    aria-label={`Order ${brand.name} on ${p.label}`}
                    data-testid={`brand-${brand.name.replace(/\s+/g, "-")}-platform-${p.label.replace(/\s+/g, "-")}`}
                  >
                    {p.label}
                  </a>
                );
              }
              return (
                <span
                  key={p.key}
                  className={`plat-btn ${p.class} plat-btn--disabled`}
                  aria-label={`${p.label} link coming soon for ${brand.name}`}
                  title="Link coming soon"
                  data-testid={`brand-${brand.name.replace(/\s+/g, "-")}-platform-${p.label.replace(/\s+/g, "-")}-disabled`}
                >
                  {p.label}
                </span>
              );
            })}
          </div>
          {brand.direct_order_url && (
            <a
              href={brand.direct_order_url}
              target="_blank"
              rel="noopener noreferrer"
              className="plat-btn direct mt-1.5"
              style={{ flex: "1 1 100%", width: "100%", padding: "10px 14px", fontSize: "11px" }}
              onClick={handleClick("Direct")}
              aria-label={`Order ${brand.name} direct via brand app`}
              data-testid={`brand-${brand.name.replace(/\s+/g, "-")}-platform-Direct`}
            >
              <ShoppingBag size={12} style={{ marginRight: 4 }} aria-hidden />
              Order Direct — {brand.name} App
            </a>
          )}
          {brand.menu_pdf_url && (
            <a
              href={`/menu/${brand.id}`}
              className="plat-btn menu-btn mt-1.5"
              style={{ flex: "1 1 100%", width: "100%", padding: "10px 14px", fontSize: "11px" }}
              onClick={() => trackEvent("menu_view", { brand_id: brand.id, brand_name: brand.name })}
              aria-label={`View ${brand.name} menu`}
              data-testid={`brand-${brand.name.replace(/\s+/g, "-")}-menu-pdf`}
            >
              <FileText size={12} style={{ marginRight: 4 }} aria-hidden />
              View Full Menu
              <ArrowUpRight size={11} style={{ marginLeft: 4 }} aria-hidden />
            </a>
          )}
        </div>

        {(brand.instagram_url || brand.facebook_url || brand.tiktok_url || brand.website_url) && (
          <>
            <div className="divider my-4" />

            {/* Footer row */}
            <div className="flex items-center justify-between mt-auto">
              <div className="flex items-center gap-3">
                {brand.instagram_url && (
                  <a
                    href={brand.instagram_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleSocial("instagram")}
                    aria-label={`${brand.name} on Instagram`}
                    className="text-muted hover:text-[var(--text)] transition-colors"
                    data-testid={`brand-${brand.name.replace(/\s+/g, "-")}-instagram`}
                  >
                    <Instagram size={16} />
                  </a>
                )}
                {brand.facebook_url && (
                  <a
                    href={brand.facebook_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleSocial("facebook")}
                    aria-label={`${brand.name} on Facebook`}
                    className="text-muted hover:text-[var(--text)] transition-colors"
                    data-testid={`brand-${brand.name.replace(/\s+/g, "-")}-facebook`}
                  >
                    <Facebook size={16} />
                  </a>
                )}
                {brand.tiktok_url && (
                  <a
                    href={brand.tiktok_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleSocial("tiktok")}
                    aria-label={`${brand.name} on TikTok`}
                    className="text-muted hover:text-[var(--text)] transition-colors"
                    data-testid={`brand-${brand.name.replace(/\s+/g, "-")}-tiktok`}
                  >
                    <TikTokIcon size={15} />
                  </a>
                )}
              </div>

              {brand.website_url && (
                <a
                  href={brand.website_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleWebsite}
                  className="font-mono text-[10px] uppercase tracking-[0.18em] inline-flex items-center gap-1 hover:text-[var(--gold)] transition-colors"
                  data-testid={`brand-${brand.name.replace(/\s+/g, "-")}-website`}
                >
                  Website
                  <ArrowUpRight size={12} />
                </a>
              )}
            </div>
          </>
        )}
      </div>
    </article>
  );
}
